import { useState } from 'react'
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LoginContainer,
  ShowInput,
  LoginForm,
  Img,
  StyledButtonLogin,
  StyledButtonCadastro,
  StyledButtonEnviarEmail,
  StyledButtonVoltar,
  InputWrapper,
  PasswordWrapper,
  PasswordInput,
  StyledShowPasswordButton,
  LoginButtonsRow,
  LoginWelcome,
  LoginDivider,
  GoogleLoginButton,
} from './styles'
import { appStatus } from '../../store/modules/app_status/actions'
import { userLogin } from '../../store/modules/login/actions';
import Logo from '../../assets/logo-vert-white.png';
import LoginBg from '../LoginBg';
import { useModalContext } from '../CustomModal/ModalProvider.jsx';
import { userService } from '../../services/userService.js';
import { fetchUsers, fetchProjects, fetchEngineeringTable } from '../../store/modules/app_data/actions.js';
import { Eye, EyeOff } from 'lucide-react';
import { publicNavigation, syncPlatformAuth } from '../../config/publicNavigation.js';
import { useThemeMode } from '../../contexts/ThemeContext';
import { persistUiSession } from '../../utils/authSession';
import { requestGoogleProfile } from '../../utils/googleAuth';
import {
  getDefaultPanelForUser,
  getDefaultRouteForUser,
} from '../../utils/userRoles';
import { getPostLoginDestination } from '../../utils/alertNotificationsReview';
import { GUARDIAN_PANEL } from '../../utils/appNavigation';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.5-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.1 29.3 4 24 4 16.1 4 9.2 8.5 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.2C29.3 35.3 26.8 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.1 39.5 16 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-3.5 5.7-6.6 7.1l.1.1 6.3 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.5-.4-3.5z" />
    </svg>
  );
}

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showError, showSuccess, showLoading: showLoadingModal, hideModal } = useModalContext();
  const [resetLoading, setResetLoading] = useState(false);
  const { applyThemeFromAuth } = useThemeMode();
  const app_status = useSelector((state) => state.app_status.status);
  const [email, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showLoading, setShowLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const finishLogin = (response) => {
    persistUiSession(response);
    syncPlatformAuth(response, response?.email || email);
    applyThemeFromAuth(response);

    setShowLoading(true);

    setTimeout(() => {
      handleLoginClick(response);
      dispatch(userLogin(null, response, response.id));
      dispatch(fetchUsers());
      dispatch(fetchProjects());
      dispatch(fetchEngineeringTable());
    }, 500);

    dispatch(
      appStatus(
        getPostLoginDestination(response)
          ? GUARDIAN_PANEL
          : getDefaultPanelForUser(response)
      )
    );
  };

  const handleSubmit = async event => {
    event.preventDefault();
    event.stopPropagation();

    try {
      // Cookie-only: tokens ficam em cookies HttpOnly; body sem access/refresh.
      const response = await userService.loginUser(email, password);
      finishLogin(response);
    } catch (error) {
      console.error('❌ Login failed:', error);

      showError(
        'Erro!',
        typeof error === 'string'
          ? error
          : error?.message || 'Não foi possível logar, verifique as informações e tente novamente.'
      );
    }

  };

  const handleGoogleLogin = async () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    try {
      const profile = await requestGoogleProfile();
      const response = await userService.loginWithGoogleWeb(profile);
      finishLogin(response);
    } catch (error) {
      console.error('❌ Google login failed:', error);
      const apiMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      showError(
        'Login com Google',
        apiMsg || 'Não foi possível entrar com o Google. Tente novamente.'
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleLoginClick = (user) => {
    const reviewDestination = getPostLoginDestination(user);
    if (reviewDestination) {
      navigate(reviewDestination.path, { state: reviewDestination.state });
      return;
    }
    navigate(getDefaultRouteForUser(user) || publicNavigation.postLogin);
  };

  const handleRegisterClick = () => {
    navigate(publicNavigation.register);
  };

  const forgotPassword = (e) => {
    e.preventDefault();
    dispatch(appStatus('forgot_password'));
  };

  const handleComeBack = (e) => {
    e.preventDefault();
    dispatch(appStatus(''));
  }

  const handleSendPasswordBack = async (e) => {
    e.preventDefault();

    if (resetLoading) return;

    if (!email || !email.trim()) {
      showError('Atenção', 'Por favor, informe seu e-mail.');
      return;
    }

    setResetLoading(true);
    showLoadingModal('Aguarde...', 'Enviando instruções de recuperação de senha.');

    try {
      await userService.resetPassword(email);
      hideModal();
      showSuccess(
        'Solicitação enviada',
        'Se existir uma conta para este e-mail, enviaremos instruções em instantes. Verifique sua caixa de entrada, spam ou lixo eletrônico.'
      );
    } catch (err) {
      hideModal();
      if (err?.response?.status === 429) {
        showError(
          'Muitas solicitações',
          'Você fez muitas tentativas. Aguarde alguns minutos antes de tentar novamente.'
        );
        return;
      }
      showSuccess(
        'Solicitação enviada',
        'Se existir uma conta para este e-mail, enviaremos instruções em instantes. Verifique sua caixa de entrada, spam ou lixo eletrônico.'
      );
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <LoginBg />
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.1 }}
      >
        <LoginContainer>
          {showLoading ? (
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
            >
              <LoginWelcome>Bem Vindo!</LoginWelcome>
            </motion.div>
          ) : (
            <>
              <div style={{ overflow: 'hidden' }}>
                <Img
                  src={Logo}
                  alt="Vert Ecotech"
                  style={{
                    filter: 'drop-shadow(3px 3px 1px rgba(255, 255, 255, 0.9))',
                    borderRadius: '10px'
                  }}
                />
              </div>
              {app_status === 'forgot_password' ? (
                <LoginForm>
                  <ShowInput
                    type="text"
                    placeholder="Email"
                    value={email}
                    onChange={event => setUsername(event.target.value)}
                  />
                  <LoginButtonsRow>
                    <StyledButtonEnviarEmail
                      type="button"
                      onClick={handleSendPasswordBack}
                      disabled={resetLoading}
                    >
                      {resetLoading ? 'Enviando...' : 'Enviar e-mail'}
                    </StyledButtonEnviarEmail>
                    <StyledButtonVoltar
                      type="button"
                      onClick={handleComeBack}
                      disabled={resetLoading}
                    >
                      Voltar
                    </StyledButtonVoltar>
                  </LoginButtonsRow>
                </LoginForm>
              ) : (
                <motion.div >
                  <LoginForm onSubmit={handleSubmit}>
                    <InputWrapper>
                      <ShowInput
                        type="text"
                        placeholder="Email"
                        value={email}
                        onChange={event => setUsername(event.target.value)}
                      />
                      <PasswordWrapper>
                        <PasswordInput
                          type={isPasswordVisible ? 'text' : 'password'}
                          placeholder="Senha"
                          value={password}
                          onChange={event => setPassword(event.target.value)}
                        />
                        <StyledShowPasswordButton
                          type="button"
                          aria-label={isPasswordVisible ? 'Ocultar senha' : 'Mostrar senha'}
                          onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                        >
                          {isPasswordVisible ? (
                            <EyeOff size={18} color="var(--text-secondary)" />
                          ) : (
                            <Eye size={18} color="var(--text-secondary)" />
                          )}
                        </StyledShowPasswordButton>
                      </PasswordWrapper>
                    </InputWrapper>


                    <LoginButtonsRow>
                      <StyledButtonLogin as="button" type="submit">Login</StyledButtonLogin>
                      <StyledButtonCadastro type="button" onClick={() => handleRegisterClick()}>Cadastre-se aqui</StyledButtonCadastro>
                    </LoginButtonsRow>

                    <LoginDivider>ou</LoginDivider>
                    <GoogleLoginButton
                      type="button"
                      onClick={handleGoogleLogin}
                      disabled={googleLoading}
                    >
                      <GoogleIcon />
                      {googleLoading ? 'Conectando…' : 'Entrar com o Google'}
                    </GoogleLoginButton>
                  </LoginForm>
                </motion.div>
              )}
              {!showLoading && app_status !== 'forgot_password' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 5 }}
                >
                  <button
                    type="button"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'white',
                      fontFamily: 'Arial',
                      fontStyle: 'italic',
                      cursor: 'pointer'
                    }}
                    onClick={forgotPassword}
                  >
                    Esqueceu a senha?
                  </button>
                </motion.div>
              )}
            </>
          )}
        </LoginContainer>
      </motion.div>
    </>
  );
};

export default Login;
